/**
 * PB CONSOLIDATED QPR REPORT COMPONENT
 * Downloads the PB consolidated QPR report for a quarter and year as-is (excel file)
 */

import { Component, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { Popup } from '../../../core/shared/components/popup/popup';
import { ReportService } from '../shared/services/report.service';

@Component({
  selector: 'app-pb-cnsldtd-qpr-rprt',
  standalone: true,
  imports: [Popup, TranslatePipe],
  template: `
    <div class="flex flex-col gap-6 p-6">
      <!-- Header Section -->
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-lg sm:text-xl font-semibold text-[#444A6D]">{{ 'REPORTS.PB_CONSOLIDATED_QPR.TITLE' | translate }}</h1>
          <p class="text-gray-600 mt-1 text-sm">{{ 'REPORTS.PB_CONSOLIDATED_QPR.SUBTITLE' | translate }}</p>
        </div>
        <button
          (click)="openFilterModal()"
          class="text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
        >
          🔍 {{ 'REPORTS.GENERATE_REPORT' | translate }}
        </button>
      </div>

      <!-- Filter Modal -->
      <app-popup
        [show]="showFilterModal"
        title="REPORTS.SELECT_QUARTER"
        (showChange)="showFilterModal = $event"
        [fields]="filterFields"
        [data]="filterData"
        (formChange)="onFilterChange($event)"
        [onSave]="onGenerateReport"
        saveButtonText="COMMON.GET_REPORT"
      ></app-popup>

      <!-- Loading State -->
      @if (isLoading) {
        <div class="flex justify-center items-center h-64">
          <div class="text-center">
            <div
              class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"
            ></div>
            <p class="mt-4 text-gray-600">{{ 'REPORTS.LOADING_REPORT' | translate }}</p>
          </div>
        </div>
      }

      <!-- Error State -->
      @if (errorMessage) {
        <div class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          ❌ {{ errorMessage | translate }}
          <button (click)="clearError()" class="ms-4 underline font-semibold">{{ 'REPORTS.DISMISS' | translate }}</button>
        </div>
      }

      <!-- Empty State -->
      @if (!isLoading && !errorMessage) {
        <div class="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg">
          <span class="text-6xl">📊</span>
          <p class="mt-4 text-xl text-gray-600">{{ 'REPORTS.NO_REPORT_DOWNLOADED' | translate }}</p>
          <p class="text-gray-500">{{ 'REPORTS.EMPTY_HINT_QUARTER_DOWNLOAD' | translate }}</p>
        </div>
      }
    </div>
  `,
})
export class PbCnsldtdQprRprtComponent {
  private reportService = inject(ReportService);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);

  showFilterModal = false;
  isLoading = false;
  errorMessage = '';

  /** The month each quarter closes on — the value the API's `qrtr` expects. */
  private readonly quarters = ['March', 'June', 'September', 'December'];

  /** Display-only labels for the quarter values above (values stay English for the API). */
  private readonly quarterLabels: Record<string, string> = {
    March: 'QPR.MARCH',
    June: 'QPR.JUNE',
    September: 'QPR.SEPTEMBER',
    December: 'QPR.DECEMBER',
  };

  /** Current year first, then the five before it — derived, never hard-coded. */
  private readonly years = Array.from(
    { length: 6 },
    (_, index) => new Date().getFullYear() - index,
  );

  // Filter form configuration
  filterFields: DynamicFieldConfig[] = [
    {
      key: 'quarter',
      label: 'REPORTS.QUARTER',
      type: 'select',
      options$: this.quarters.map((quarter) => ({ label: this.quarterLabels[quarter], value: quarter })),
      validations: { required: true },
      gridClass: 'col-span-1',
    },
    {
      key: 'year',
      label: 'REPORTS.YEAR',
      type: 'select',
      options$: this.years.map((year) => ({ label: String(year), value: year })),
      validations: { required: true },
      gridClass: 'col-span-1',
    },
  ];

  filterData: { quarter: string; year: number | null } = {
    quarter: '',
    year: null,
  };

  /**
   * Open filter modal with an empty form every time
   */
  openFilterModal(): void {
    this.filterData = { quarter: '', year: null };
    this.showFilterModal = true;
  }

  /**
   * Handle filter form changes
   */
  onFilterChange(data: any): void {
    this.filterData = data;
  }

  /**
   * Generate report - returns a function for the popup component
   */
  onGenerateReport = (data: any): void => {
    this.generateReport(data);
  };

  /**
   * Main report download logic
   */
  private generateReport(filterData: any): void {
    const quarter = filterData?.quarter;
    const year = Number(filterData?.year);

    if (!quarter || !year) {
      this.errorMessage = 'REPORTS.SELECT_QUARTER_AND_YEAR';
      this.toastr.error(this.translate.instant('REPORTS.SELECT_QUARTER_AND_YEAR'));
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.reportService.getPbConsolidatedQprReport(quarter, year).subscribe({
      next: (blob) => {
        this.isLoading = false;
        // API returns the real .xlsx workbook binary — save it as-is.
        saveAs(blob, `PbConsolidatedQprReport_${quarter}_${year}.xlsx`);
        this.toastr.success(this.translate.instant('REPORTS.DOWNLOAD_SUCCESS'));
      },
      error: (error) => {
        this.isLoading = false;
        const errMsg =
          error?.error?.message || error?.message || 'REPORTS.GENERATE_FAILED';
        this.errorMessage = errMsg;
        this.toastr.error(this.translate.instant(errMsg));
      },
    });
  }

  /**
   * Clear error message
   */
  clearError(): void {
    this.errorMessage = '';
  }
}
